import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import {
  Alert,
  Button,
  Card,
  DataRow,
  ProgressBar,
  Skeleton,
  SkeletonGroup,
} from '@/components/ui';
import { Dropzone } from '@/features/upload/Dropzone';
import { useFileUpload } from '@/features/upload/useFileUpload';
import { useAnnouncer } from '@/lib/a11y';
import { useMeta } from '@/lib/api/meta';
import { queryKeys } from '@/lib/api/queryClient';
import type { UploadResult } from '@/lib/api/types';
import { formatCount, formatExtensionList, formatFileSize, formatMegabytes } from '@/lib/format';

import { stepUrl } from './steps';
import { useOrderStep } from './useOrderStep';

/**
 * /order/upload — pick a PDF and get it to the server.
 *
 * The limits panel is rendered from GET /api/v1/meta rather than from constants,
 * so the client can never promise an upload the API will refuse. The same
 * `meta.upload` object drives the `accept` attribute and the pre-checks.
 *
 * The instant the server answers — 201 with a report or 202 with a poll target —
 * the upload id goes into the URL and we navigate. That is the point at which
 * the customer's work stops being losable: from here a refresh, a backgrounded
 * tab or a dropped connection all resume from `?u=`.
 */
export function UploadStep() {
  const { step, stepper, urlState } = useOrderStep();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { announce } = useAnnouncer();
  const { data: meta, isPending: metaPending, isError: metaFailed } = useMeta();

  const handleUploaded = useCallback(
    (result: UploadResult, file: File) => {
      // Seed the cache so the Review step renders the report it already has
      // instead of re-fetching a document we were just handed.
      queryClient.setQueryData(queryKeys.upload(result.upload_id), result);
      announce(`${file.name} uploaded. Checking your file.`);
      navigate(stepUrl('review', { ...urlState, upload: result.upload_id }));
    },
    [announce, navigate, queryClient, urlState],
  );

  const { state, start, cancel, retry, reset } = useFileUpload({
    limits: meta?.upload,
    onUploaded: handleUploaded,
  });

  const uploading = state.status === 'uploading';
  const failure = state.error;

  const accept = meta
    ? [...meta.upload.accepted_extensions, ...meta.upload.accepted_mime_types].join(',')
    : 'application/pdf,.pdf';

  return (
    <AppShell
      title={step.title}
      subtitle="We check every file before printing, so you know what you are paying for."
      progress={stepper}
      backTo="/"
      // Fixed width whether or not the action is showing: it appears only while
      // uploading, and a column that changes width mid-upload is a jolt.
      wide
      action={
        uploading ? (
          <Button variant="secondary" fullWidth onClick={cancel}>
            Cancel upload
          </Button>
        ) : failure ? (
          <div className="flex flex-col gap-2">
            {failure.retryable ? (
              <Button fullWidth onClick={retry}>
                Try again
              </Button>
            ) : null}
            <Button variant={failure.retryable ? 'secondary' : 'primary'} fullWidth onClick={reset}>
              Choose another file
            </Button>
          </div>
        ) : undefined
      }
    >
      {/* Side by side on desktop: the dropzone is the whole job, and the limits
          are a reference you read WHILE choosing a file rather than after
          scrolling past it. */}
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start lg:gap-6">
        {failure ? (
          <Alert
            severity="danger"
            title="We could not upload that file"
            live
            className="lg:col-span-2"
          >
            <p>{failure.message}</p>
            {state.file ? (
              <p className="text-ink-subtle">
                {state.file.name} &middot; {formatFileSize(state.file.size)}
              </p>
            ) : null}
            {failure.requestId ? (
              <p className="text-xs text-ink-subtle">
                Reference:{' '}
                <code className="select-all font-mono">{failure.requestId.slice(0, 12)}</code>
              </p>
            ) : null}
          </Alert>
        ) : null}

        {uploading && state.file ? (
          <Card title="Uploading">
            <div className="flex flex-col gap-3">
              <p className="truncate text-sm font-medium text-ink">{state.file.name}</p>
              {/* Real bytes-on-the-wire progress from XMLHttpRequest upload events
                  — see lib/api/uploads.ts for why fetch cannot do this. */}
              <ProgressBar
                label={state.percent === 100 ? 'Checking your file' : 'Upload progress'}
                value={state.percent}
                showValue
              />
              <p className="text-sm text-ink-muted">
                {state.percent === 100
                  ? 'Uploaded. We are checking the pages now.'
                  : `${formatFileSize(state.file.size)} — keep this page open.`}
              </p>
            </div>
          </Card>
        ) : (
          <Dropzone
            accept={accept}
            onFile={(file) => void start(file)}
            hint={
              meta
                ? `${formatExtensionList(meta.upload.accepted_extensions)} up to ${formatMegabytes(meta.upload.max_upload_mb)}`
                : 'PDF files'
            }
          />
        )}

        <Card title="What we accept">
          {metaPending ? (
            <SkeletonGroup label="Loading upload limits">
              <div className="flex flex-col gap-3">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-3/5" />
              </div>
            </SkeletonGroup>
          ) : metaFailed || !meta ? (
            // Non-blocking failure: the limits are guidance, and the server
            // enforces them anyway, so this degrades to a note, not an error screen.
            <p className="text-sm text-ink-muted">
              We couldn&rsquo;t load the current limits. You can still continue &mdash; we&rsquo;ll
              check your file when you upload it.
            </p>
          ) : (
            <dl>
              <DataRow
                label="File type"
                value={formatExtensionList(meta.upload.accepted_extensions)}
              />
              <DataRow label="Maximum size" value={formatMegabytes(meta.upload.max_upload_mb)} />
              {/* See HomePage: 0 disables the warning, so "up to 0" would be
                  both wrong and discouraging. */}
              <DataRow
                label="Pages"
                value={
                  meta.upload.warn_page_count > 0
                    ? `Up to ${formatCount(meta.upload.warn_page_count)} without a check`
                    : 'Any length'
                }
              />
              <DataRow label="Paper sizes" value={meta.paper_sizes.join(', ')} />
            </dl>
          )}
        </Card>
      </div>
    </AppShell>
  );
}
